import React, { useState, useEffect } from 'react';
import { StudentFee, FeeFilters, FeeStats } from '../types/fees';
import { Student } from '../types';
import { feesApi } from '../services/feesApi';
import { studentApi } from '../services/api';
import FeesTable from '../components/Fees/FeesTable';
import PaymentModal from '../components/Fees/PaymentModal';
import InvoiceCreator from '../components/Fees/InvoiceCreator';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import './FeesManagement.css';

const FeesManagement: React.FC = () => {
  const { user } = useAuth();
  const [filters, setFilters] = useState<FeeFilters>({
    status: 'all',
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  const [students, setStudents] = useState<Student[]>([]);
  const [stats, setStats] = useState<FeeStats | null>(null);
  const [selectedFee, setSelectedFee] = useState<StudentFee | null>(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isInvoiceCreatorOpen, setIsInvoiceCreatorOpen] = useState(false);

  useEffect(() => {
    fetchStudents();
    fetchStats();
  }, []);

  const fetchStudents = async () => {
    try {
      // Fetch a large page size so every student is available in the filter dropdown
      const response = await studentApi.getAll({ active: true, limit: 1000 });
      setStudents(response.data.students);
    } catch (error: any) {
      console.error('Error fetching students:', error);
      toast.error('Failed to fetch students');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await feesApi.getStats();
      setStats(response.data);
    } catch (error: any) {
      console.error('Error fetching fee stats:', error);
      // Don't show error toast for stats as it's not critical
    }
  };

  const handleFilterChange = (newFilters: Partial<FeeFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
  };

  const handlePaymentClick = (fee: StudentFee) => {
    setSelectedFee(fee);
    setIsPaymentModalOpen(true);
  };

  const handlePaymentSuccess = () => {
    fetchStats();
    setRefreshKey(prev => prev + 1);
  };

  const handleGenerateMonthlyFees = async () => {
    if (!window.confirm('Are you sure you want to generate monthly fees for all active students?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await feesApi.generateMonthlyFees();
      toast.success(response.message);
      fetchStats();
    } catch (error: any) {
      console.error('Error generating monthly fees:', error);
      toast.error(error.error || 'Failed to generate monthly fees');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOverdueStatuses = async () => {
    try {
      setLoading(true);
      const response = await feesApi.updateOverdueStatuses();
      toast.success(response.message);
      fetchStats();
    } catch (error: any) {
      console.error('Error updating overdue statuses:', error);
      toast.error(error.error || 'Failed to update overdue statuses');
    } finally {
      setLoading(false);
    }
  };

  const handleResetFees = async () => {
    const confirmation = window.prompt('Type RESET_FEES to delete all fees and payments. This action cannot be undone.');
    if (confirmation !== 'RESET_FEES') {
      if (confirmation !== null) {
        toast.info('Reset cancelled. Confirmation phrase did not match.');
      }
      return;
    }

    try {
      setLoading(true);
      await feesApi.resetAll(confirmation);
      toast.success('All fees and payments have been cleared.');
      fetchStats();
      setFilters(prev => ({ ...prev }));
    } catch (error: any) {
      console.error('Error resetting fees:', error);
      let errorMessage = 'Failed to reset fees';
      if (error?.error) {
        errorMessage = error.error;
      } else if (error?.message) {
        try {
          const parsed = JSON.parse(error.message);
          errorMessage = parsed.error || errorMessage;
        } catch {
          errorMessage = error.message;
        }
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const clearFilters = () => {
    setFilters({
      status: 'all',
      page: 1,
      limit: 20,
      sortBy: 'createdAt',
      sortOrder: 'desc'
    });
  };

  return (
    <div className="fees-management">
      <div className="fees-management-header">
        <div className="header-content">
          <h1>Fees Management</h1>
          <p>Manage student fees, record payments, and track overdue accounts</p>
        </div>
        <div className="header-actions">
          {user?.is_superadmin && (
            <button
              className="btn btn-danger"
              onClick={handleResetFees}
              disabled={loading}
            >
              🗑️ Reset Fees
            </button>
          )}
          <button
            className="btn btn-secondary"
            onClick={handleUpdateOverdueStatuses}
            disabled={loading}
          >
            ⏰ Update Overdue
          </button>
          <button
            className="btn btn-primary"
            onClick={handleGenerateMonthlyFees}
            disabled={loading}
          >
            💰 Generate Monthly Fees
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setIsInvoiceCreatorOpen(true)}
            disabled={loading}
          >
            🧾 Create Invoice
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card total">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <div className="stat-number">{stats.totalFees}</div>
              <div className="stat-label">Total Fees</div>
              <div className="stat-amount">{formatCurrency(stats.totalAmount)}</div>
            </div>
          </div>

          <div className="stat-card paid">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <div className="stat-number">{stats.paidFees}</div>
              <div className="stat-label">Paid Fees</div>
              <div className="stat-amount">{formatCurrency(stats.paidAmount)}</div>
            </div>
          </div>

          <div className="stat-card pending">
            <div className="stat-icon">⏳</div>
            <div className="stat-content">
              <div className="stat-number">{stats.pendingFees}</div>
              <div className="stat-label">Pending Fees</div>
              <div className="stat-amount">{formatCurrency(stats.pendingAmount)}</div>
            </div>
          </div>

          <div className="stat-card overdue">
            <div className="stat-icon">⚠️</div>
            <div className="stat-content">
              <div className="stat-number">{stats.overdueFees}</div>
              <div className="stat-label">Overdue Fees</div>
              <div className="stat-amount">{formatCurrency(stats.overdueAmount)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-header">
          <h3>Filter Fees</h3>
          <button className="clear-filters-btn" onClick={clearFilters}>
            Clear Filters
          </button>
        </div>

        <div className="filters-grid">
          <div className="filter-group">
            <label htmlFor="status-filter">Status</label>
            <select
              id="status-filter"
              value={filters.status || 'all'}
              onChange={(e) => handleFilterChange({ status: e.target.value as any })}
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="student-filter">Student</label>
            <select
              id="student-filter"
              value={filters.studentId || ''}
              onChange={(e) => handleFilterChange({ 
                studentId: e.target.value ? parseInt(e.target.value) : undefined 
              })}
            >
              <option value="">All Students</option>
              {students.map(student => (
                <option key={student.id} value={student.id}>
                  {student.first_name} {student.last_name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="start-date-filter">From Date</label>
            <input
              type="date"
              id="start-date-filter"
              value={filters.startDate || ''}
              onChange={(e) => handleFilterChange({ startDate: e.target.value || undefined })}
            />
          </div>

          <div className="filter-group">
            <label htmlFor="end-date-filter">To Date</label>
            <input
              type="date"
              id="end-date-filter"
              value={filters.endDate || ''}
              onChange={(e) => handleFilterChange({ endDate: e.target.value || undefined })}
            />
          </div>

          <div className="filter-group">
            <label htmlFor="sort-filter">Sort By</label>
            <select
              id="sort-filter"
              value={`${filters.sortBy}-${filters.sortOrder}`}
              onChange={(e) => {
                const [sortBy, sortOrder] = e.target.value.split('-');
                handleFilterChange({ sortBy: sortBy as any, sortOrder: sortOrder as any });
              }}
            >
              <option value="createdAt-desc">Newest First</option>
              <option value="createdAt-asc">Oldest First</option>
              <option value="dueDate-asc">Due Date (Earliest)</option>
              <option value="dueDate-desc">Due Date (Latest)</option>
              <option value="amount-desc">Amount (High to Low)</option>
              <option value="amount-asc">Amount (Low to High)</option>
              <option value="studentName-asc">Student Name (A-Z)</option>
              <option value="studentName-desc">Student Name (Z-A)</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="limit-filter">Items per Page</label>
            <select
              id="limit-filter"
              value={filters.limit || 20}
              onChange={(e) => handleFilterChange({ limit: parseInt(e.target.value) })}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      {/* Fees Table */}
      <div className="fees-table-section">
        <FeesTable
          filters={filters}
          onPaymentClick={handlePaymentClick}
          onRefresh={fetchStats}
          refreshKey={refreshKey}
        />
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        fee={selectedFee}
        onPaymentSuccess={handlePaymentSuccess}
      />

      {/* Invoice Creator */}
      {isInvoiceCreatorOpen && (
        <InvoiceCreator
          onClose={() => setIsInvoiceCreatorOpen(false)}
        />
      )}
    </div>
  );
};

export default FeesManagement;
